/**
 * Webflow's breakpoint cascade.
 *
 * `main` is the base (desktop) breakpoint, and styles cascade **outward in both directions**:
 *
 *     xxl(≥1920) ← xl(≥1440) ← large(≥1280) ← [main] → medium(≤991) → small(≤767) → tiny(≤479)
 *
 * This is the part that is easy to get subtly wrong. A declaration at `small` must be diffed
 * against *main ⊕ medium*, not against main alone. Diff against main and you emit a redundant
 * `font-size` at `small` that is identical to `medium`'s — harmless until a marketer edits the
 * tablet size in the Designer and the mobile size refuses to follow, because it now carries its
 * own explicit override that was never meant to exist.
 */

import type { Longhand } from './longhand.js';

export type WfBreakpoint = 'xxl' | 'xl' | 'large' | 'main' | 'medium' | 'small' | 'tiny';

export type WfPseudo =
  | 'hover'
  | 'active'
  | 'focus'
  | 'focus-visible'
  | 'focus-within'
  | 'visited'
  | 'placeholder'
  | 'before'
  | 'after'
  | 'first-child'
  | 'last-child';

/**
 * A variant key: a breakpoint, a pseudo-state, or both joined by an underscore.
 *
 * Authors may write a bare pseudo (`hover`) for convenience, but that is NOT the wire format —
 * see `toEmitVariantKey`. `main` alone is excluded deliberately: base-breakpoint declarations
 * belong in `StyleDef.base`, which compiles to `styleLess`, not to a variant.
 */
export type WfVariantKey =
  | Exclude<WfBreakpoint, 'main'>
  | WfPseudo
  | `${WfBreakpoint}_${WfPseudo}`;

/** Media query per breakpoint. `main` has none — it is the base. */
export const BREAKPOINT_QUERY: Record<Exclude<WfBreakpoint, 'main'>, string> = {
  xxl: '(min-width: 1920px)',
  xl: '(min-width: 1440px)',
  large: '(min-width: 1280px)',
  medium: '(max-width: 991px)',
  small: '(max-width: 767px)',
  tiny: '(max-width: 479px)',
};

/** Inheritance chain walking *up* from main (larger screens). */
const UP_CHAIN: WfBreakpoint[] = ['main', 'large', 'xl', 'xxl'];

/** Inheritance chain walking *down* from main (smaller screens). */
const DOWN_CHAIN: WfBreakpoint[] = ['main', 'medium', 'small', 'tiny'];

/**
 * Source order for emitted CSS.
 *
 * Within each direction, later rules must win: at 2000px all three min-width queries match,
 * so `xxl` has to come last. At 400px all three max-width queries match, so `tiny` has to
 * come last. The two groups never overlap, so their relative order is arbitrary.
 */
export const CSS_EMIT_ORDER: Exclude<WfBreakpoint, 'main'>[] = [
  'large',
  'xl',
  'xxl',
  'medium',
  'small',
  'tiny',
];

/** Order variants are written into the XscpData `variants` object. Cosmetic, but stable diffs. */
export const VARIANT_EMIT_ORDER: WfBreakpoint[] = [
  'large',
  'xl',
  'xxl',
  'medium',
  'small',
  'tiny',
];

export function isBreakpoint(key: string): key is WfBreakpoint {
  return (
    key === 'main' || key === 'xxl' || key === 'xl' || key === 'large' ||
    key === 'medium' || key === 'small' || key === 'tiny'
  );
}

/**
 * Split `medium_hover` into its parts. A bare pseudo implies the `main` breakpoint.
 *
 * Splits on the FIRST underscore only, because a state may itself contain a hyphen or more —
 * `tiny_focus-visible` appears in real captured payloads.
 */
export function parseVariantKey(key: WfVariantKey): {
  breakpoint: WfBreakpoint;
  pseudo?: WfPseudo;
} {
  const idx = key.indexOf('_');
  if (idx === -1) {
    return isBreakpoint(key)
      ? { breakpoint: key }
      : { breakpoint: 'main', pseudo: key as WfPseudo };
  }
  return {
    breakpoint: key.slice(0, idx) as WfBreakpoint,
    pseudo: key.slice(idx + 1) as WfPseudo,
  };
}

/**
 * The key Webflow actually expects on the wire.
 *
 * A pseudo-state is ALWAYS prefixed with its breakpoint, so the base-breakpoint hover state is
 * `main_hover`, never a bare `hover`. This matters: Webflow reads the segment before the
 * underscore as the breakpoint name, so a bare `hover` parses as a breakpoint *called* "hover",
 * fails to match, and falls back to base — silently discarding the hover styling.
 *
 * Confirmed against a real captured payload containing `main_hover`, `tiny_focus`,
 * `tiny_active`, `tiny_focus-visible`, `tiny_visited` and `tiny_hover`.
 */
export function toEmitVariantKey(key: WfVariantKey): string {
  const { breakpoint, pseudo } = parseVariantKey(key);
  return pseudo ? `${breakpoint}_${pseudo}` : breakpoint;
}

/**
 * The breakpoints a given breakpoint inherits from, nearest ancestor last.
 *
 * `main` inherits from nothing. `small` inherits from `['main', 'medium']`.
 */
export function ancestorsOf(bp: WfBreakpoint): WfBreakpoint[] {
  if (bp === 'main') return [];
  const chain = UP_CHAIN.includes(bp) ? UP_CHAIN : DOWN_CHAIN;
  const idx = chain.indexOf(bp);
  return chain.slice(0, idx);
}

export interface StyleSource {
  base: Longhand;
  variants?: Partial<Record<WfVariantKey, Longhand>>;
}

/**
 * The declarations in effect at a breakpoint before that breakpoint's own rules apply —
 * i.e. everything it inherits.
 */
export function inheritedAt(src: StyleSource, bp: WfBreakpoint): Longhand {
  let acc: Longhand = { ...src.base };
  for (const ancestor of ancestorsOf(bp)) {
    if (ancestor === 'main') continue; // already seeded from base
    Object.assign(acc, src.variants?.[ancestor] ?? {});
  }
  return acc;
}

/** The fully resolved declarations in effect at a breakpoint, including its own rules. */
export function effectiveAt(src: StyleSource, bp: WfBreakpoint): Longhand {
  return { ...inheritedAt(src, bp), ...(bp === 'main' ? {} : (src.variants?.[bp] ?? {})) };
}

/**
 * Properties in `declared` that actually change something relative to `inherited`.
 *
 * This is what strips the redundant declarations that would otherwise break breakpoint
 * inheritance once a marketer starts editing in the Designer.
 */
export function diffLonghand(inherited: Longhand, declared: Longhand): Longhand {
  const out: Longhand = {};
  for (const [prop, value] of Object.entries(declared) as [keyof Longhand, string][]) {
    if (value === undefined || value === '') continue;
    if (inherited[prop] !== value) out[prop] = value;
  }
  return out;
}

/**
 * Resolve a style's breakpoint variants down to minimal deltas.
 *
 * Pseudo-state variants pass through untouched: a `:hover` rule is an addition to whatever
 * is in effect, not a breakpoint override, so diffing it against the base would strip exactly
 * the declarations that make it a hover state.
 */
export function resolveCascade(
  src: StyleSource,
): Partial<Record<WfVariantKey, Longhand>> {
  const out: Partial<Record<WfVariantKey, Longhand>> = {};
  if (!src.variants) return out;

  for (const [rawKey, declared] of Object.entries(src.variants) as [WfVariantKey, Longhand][]) {
    if (!declared || Object.keys(declared).length === 0) continue;

    const { breakpoint, pseudo } = parseVariantKey(rawKey);

    if (pseudo) {
      out[rawKey] = { ...declared };
      continue;
    }

    const delta = diffLonghand(inheritedAt(src, breakpoint), declared);
    if (Object.keys(delta).length > 0) out[rawKey] = delta;
  }

  return out;
}
